import React from "react";
import {
    TextField,
    makeStyles,
    Theme,
    createStyles,
    Button,
    CircularProgress,
    Typography
} from "@material-ui/core";
import { useSnackbar } from "notistack";
import { useDataUnlessForbidden, apiPost } from "../../services/api";
import IUser from "../../models/IUser";
import Loading from "../shared/Loading";
import { useAuth } from "../../services/authentication";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        viewProjectButton: {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,

            padding: theme.spacing(1, 10, 1, 10),
            fontSize: "1rem",
            "&:hover": {
                backgroundColor: theme.palette.primary.dark
            },
            "&:disabled": {
                backgroundColor: "#bdbdbd",
                color: "#f5f5f5"
            }
        }
    })
);

export interface IUserProfileProps {
    userId?: string;
    onProfileUpdated?(): void;
}

export const UserProfileForm: React.FC<IUserProfileProps> = props => {
    const [email, setEmail] = React.useState("");
    const [givenName, setGivenName] = React.useState("");
    const [familyName, setFamilyName] = React.useState("");
    const [phoneNumber, setPhoneNumber] = React.useState("");
    const classes = useStyles();

    const { enqueueSnackbar } = useSnackbar();
    const [user, loadUser] = useDataUnlessForbidden<IUser>(
        props.userId ? `users/all/${props.userId}` : `users/me`,
        () =>
            enqueueSnackbar(
                "We couldn't retrieve user profile. Please log out and try again.",
                { variant: "error" }
            )
    );

    const onEmailChanged = (event: {
        target: { value: React.SetStateAction<string> };
    }) => {
        setEmail(event.target.value);
    };

    const onGivenNameChanged = (event: {
        target: { value: React.SetStateAction<string> };
    }) => {
        setGivenName(event.target.value);
    };

    const onFamilyNameChanged = (event: {
        target: { value: React.SetStateAction<string> };
    }) => {
        setFamilyName(event.target.value);
    };

    const onPhoneNumberChanged = (event: {
        target: { value: React.SetStateAction<string> };
    }) => {
        setPhoneNumber(event.target.value);
    };

    React.useEffect(() => {
        if (user) {
            setEmail(user.email);
            setFamilyName(user.lastName);
            setGivenName(user.firstName);
            setPhoneNumber(user.phoneNumber);

            setCanUpdateProfile(true);
        }
    }, [user, loadUser]);

    const [canUpdateProfile, setCanUpdateProfile] = React.useState(false);

    const { refresh } = useAuth();

    const updateProfile = async () => {
        const profile = {
            email,
            firstName: givenName,
            lastName: familyName,
            phoneNumber
        };

        if (props.userId) {
            await apiPost(`users/all/${props.userId}`, profile);
        } else {
            await apiPost(`users/me`, profile);
        }

        enqueueSnackbar("Profile updated.", { variant: "success" });

        if (!props.userId) {
            if (refresh) {
                refresh();
            }
        }

        if (props.onProfileUpdated) {
            props.onProfileUpdated();
        }
    };

    React.useEffect(() => {
        if (givenName && familyName) {
            setCanUpdateProfile(true);
        } else {
            setCanUpdateProfile(false);
        }
    }, [email, givenName, familyName, phoneNumber]);

    return (
        <>
            {loadUser && (
                <>
                    <Loading>
                        <CircularProgress />
                    </Loading>
                    <Typography
                        variant="subtitle1"
                        style={{
                            position: "absolute",
                            left: "-15px",
                            top: "60px"
                        }}
                    >
                        Loading Profile...
                    </Typography>
                </>
            )}

            {user && (
                <>
                    <React.Fragment>
                        <TextField
                            label="E-Mail Address"
                            onChange={onEmailChanged}
                            type="email"
                            variant="filled"
                            value={email}
                            required
                            fullWidth
                            inputProps={{ maxLength: 450, readOnly: true }}
                        />
                        <br />
                        <br />
                        <TextField
                            label="First Name"
                            onChange={onGivenNameChanged}
                            variant="outlined"
                            value={givenName}
                            required
                            fullWidth
                            inputProps={{ maxLength: 1024 }}
                        />
                        <br />
                        <br />
                        <TextField
                            label="Last Name"
                            onChange={onFamilyNameChanged}
                            variant="outlined"
                            value={familyName}
                            required
                            fullWidth
                            inputProps={{ maxLength: 1024 }}
                        />
                        <br />
                        <br />
                        <TextField
                            label="Phone Number"
                            onChange={onPhoneNumberChanged}
                            variant="outlined"
                            value={phoneNumber}
                            fullWidth
                            inputProps={{ maxLength: 1024 }}
                        />
                    </React.Fragment>
                    <br />
                    <br />
                    <Button
                        onClick={updateProfile}
                        disabled={!canUpdateProfile}
                        className={classes.viewProjectButton}
                    >
                        Update Profile
                    </Button>
                </>
            )}
        </>
    );
};

export default UserProfileForm;
